import {
  ApolloError,
  LazyQueryExecFunction,
  LazyQueryHookOptions,
  LazyQueryResult,
  QueryLazyOptions,
  TypedDocumentNode,
  useLazyQuery,
} from '@apollo/client'
import { DocumentNode } from 'graphql'
import { useContext } from 'react'

import { ApolloClientsContext } from '../../graphql/apollo'

export type SearchLazyQueryTuple<TData, TVariables> = [
  /*
   * Disallow setting context in execute function to support use of refetch().
   *
   * @apollo/client 3.6.x no longer uses a "deep" merge to combine hook and execute
   * options. Consequently, any `context` prop specified in execute options overrides
   * the entire `context` prop in query options.
   *
   * Ref: https://github.com/apollographql/apollo-client/blob/v3.6.8/src/react/hooks/useLazyQuery.ts#L37
   *
   * When calling `refetch` due to context/token changes, any `context` prop specified
   * previously passed to the execute function will continue to override the hook
   * option's `context` prop.
   */
  (
    options?: Omit<QueryLazyOptions<TVariables>, 'context'>,
  ) => ReturnType<LazyQueryExecFunction<TData, TVariables>>,
  Omit<LazyQueryResult<TData, TVariables>, 'error'> & {
    error?: ApolloError | Error
  },
]

export const useSearchLazyQuery = <TData = unknown, TVariables = Record<string, unknown>>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  hookOptions?: LazyQueryHookOptions<TData, TVariables>,
): SearchLazyQueryTuple<TData, TVariables> => {
  const { comma: commaClient } = useContext(ApolloClientsContext)
  const [runLazyQuery, queryResultProps] = useLazyQuery(query, {
    ...hookOptions,
    client: commaClient,
    context: {
      ...(hookOptions && hookOptions.context),
      useLatestTokenIfLoggedIn: true,
      // set context in hook options so refetched queries will include token
    },
  })

  return [runLazyQuery, queryResultProps]
}
